import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Customer } from '../model/customer.model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DropdownService } from '../../dropdowns/dropdown.service';
import { SpinnerService } from '../../service/spinner.service';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { Observable, of } from 'rxjs/index';
import { Option } from '../../dropdowns/models/option.model';
import { defaultSearchDelay, COUNTRIES_WITH_STATES } from '../../constants';
import { CustomerService } from '../services/customer.service';
import { delay, finalize } from 'rxjs/internal/operators';
import { CountryStateCrossRequired } from '../../shared/validation/country-state-cross-required.validator';

@Component({
  selector: 'customer-form',
  templateUrl: './customer-form.component.html'
})
export class CustomerFormComponent implements OnInit {

  @Output()
  canceled = new EventEmitter();

  @Output()
  accepted: EventEmitter<Customer> = new EventEmitter<Customer>();

  customerForm: FormGroup;
  customer: Customer;

  countryList$: Observable<Option[]>;
  stateList$: Observable<Option[]>[] = [];
  specialFlagList$: Observable<Option[]>;

  isStateRequired: boolean[] = [];

  private readonly customerFormControlsConfig = {
      name: ['', [Validators.required, Validators.maxLength(30)]],
      dbaName: ['', [Validators.maxLength(30)]],
      contact: ['', Validators.maxLength(30)],
      address1: ['', [Validators.required, Validators.maxLength(25)]],
      address2: ['', Validators.maxLength(25)],
      city: ['', [Validators.required, Validators.maxLength(15)]],
      state: [null, null],
      postalCode: ['', [Validators.maxLength(15)]],
      country: [null, Validators.required],
      phone: ['', Validators.maxLength(20)],
      fax: ['', Validators.maxLength(20)],
      email: ['', [Validators.required, Validators.maxLength(45), Validators.email]],
      specialFlag: [null, null],
      acctMgr: ['', Validators.maxLength(30)]
  };

  private readonly customerFormValidators = {
    validator: Validators.compose([
      CountryStateCrossRequired('country', 'state', 'stateRequiredCrossValidation')
    ])
  };

  constructor(private dropdownService: DropdownService,
              private formBuilder: FormBuilder,
              private spinnerService: SpinnerService,
              private flashMessagesService: FlashMessagesService,
              private customerService: CustomerService) {
  }

  ngOnInit() {
    this.reset();
    this.initDropdowns();
  }

  initDropdowns() {
    this.countryList$ = this.dropdownService.getAllCodes('country');
    this.specialFlagList$ = this.dropdownService.getAllCodes('specialflag');
  }

  onCountryChanged(countryControlName: string, stateControlName: string) {
    const countryValue = this.customerForm.controls[countryControlName].value;
    this.customerForm.controls[stateControlName].setValue(null);
    let observable$: Observable<Option[]>;
    if (countryValue) {
      observable$ = this.dropdownService.getStatesByCountryCode(countryValue);
    } else {
      observable$ = of([]);
    }
    this.stateList$[countryControlName] = observable$;
    this.setStateRequired(countryControlName);
  }

  setStateRequired(countryControlName?: string) {
    this.isStateRequired[countryControlName] = COUNTRIES_WITH_STATES.includes(this.customerForm.controls[countryControlName].value);
  }

  onSubmit() {
    this.customer = Object.assign(this.customerForm.value);

    this.spinnerService.startSpinner();
    this.customer.customerNumber = null;
    this.customerService.add(this.customer).pipe(
      delay(defaultSearchDelay),
      finalize(() => this.spinnerService.stopSpinner())
    ).subscribe((data) => {
        this.customer = data['output'];
        if (data['successful']) {
          if (this.customer.validations && !this.customer.validations.isValid) {
            // Data is not valid
            return this.validationErrors();
          } else {
            this.flashMessagesService.success('Customer successfully created. Customer Number: ' + this.customer.customerNumber);
            this.accepted.next(this.customer);
          }
        }
      },
      error => {
        this.customerService.handleError('addCustomer', error);
      });
  }

  /**
   * Extract the validation errors
   *
   * @returns {boolean}
   */
  private validationErrors() {
    this.customer.validations.fieldErrors.forEach(error => {
      this.flashMessagesService.error(error.errorMsg);
    });
    return false;
  }

  cancel() {
    this.canceled.emit();
    this.reset();
  }

  reset() {
    this.stateList$ = [];
    if (!this.customerForm) {
      this.createForm();
    } else {
      this.customerForm.reset({
        name: '',
        dbaName: '',
        contact: '',
        address1: '',
        address2: '',
        city: '',
        state: null,
        postalCode: '',
        country: null,
        phone: '',
        fax: '',
        email: '',
        specialFlag: null,
        acctMgr: ''
      });
    }
    this.customerForm.enable();
  }

  private createForm() {
    this.customerForm = this.formBuilder.group(this.customerFormControlsConfig, this.customerFormValidators);
  }

}
